import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
    Search, Bell, Sun, Moon, LogOut, UserCircle, Settings as SettingsIcon,
    Sparkles, HelpCircle, ChevronDown,
} from "lucide-react";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import tr from "@/i18n/tr";
import { cn } from "@/lib/utils";

function initials(name) {
    if (!name) return "OMB";
    return name.split(" ").filter(Boolean).slice(0, 2).map(n => n[0]?.toUpperCase()).join("");
}

export default function TopBar({ onOpenSidebar }) {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const { theme, toggle: toggleTheme } = useTheme();
    const [q, setQ] = useState("");

    const handleLogout = async () => {
        await logout();
        navigate("/login", { replace: true });
    };

    return (
        <header className="sticky top-0 z-30 flex h-[68px] items-center gap-3 border-b border-border bg-background/85 backdrop-blur-md px-4 md:px-6">
            {/* mobile menu */}
            <button
                type="button"
                onClick={onOpenSidebar}
                data-testid="topbar-open-sidebar"
                className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-xl border border-border text-foreground hover:bg-muted"
                aria-label="Menüyü aç"
            >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="3" y1="6" x2="21" y2="6" />
                    <line x1="3" y1="12" x2="21" y2="12" />
                    <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
            </button>

            {/* Global search */}
            <div className="relative flex-1 max-w-xl">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                    type="text"
                    placeholder={tr.common.search}
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    data-testid="topbar-search"
                    className={cn(
                        "h-10 w-full rounded-xl border border-border bg-muted/60 pl-10 pr-16 text-sm text-foreground",
                        "placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand/40 transition-all"
                    )}
                />
                <div className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 hidden md:flex items-center gap-1">
                    <span className="kbd">⌘</span>
                    <span className="kbd">K</span>
                </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
                {/* AI shortcut */}
                <Link
                    to="/module/ai-assistant"
                    data-testid="topbar-ai"
                    className="hidden sm:inline-flex h-10 w-10 items-center justify-center rounded-xl border border-border text-foreground hover:bg-muted transition-colors"
                    aria-label="AI Asistan"
                >
                    <Sparkles className="h-[18px] w-[18px]" />
                </Link>

                {/* Notifications */}
                <button
                    type="button"
                    data-testid="topbar-notifications"
                    className="relative inline-flex h-10 w-10 items-center justify-center rounded-xl border border-border text-foreground hover:bg-muted transition-colors"
                    aria-label="Bildirimler"
                >
                    <Bell className="h-[18px] w-[18px]" />
                    <span className="absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-[hsl(var(--brand))] px-1 text-[10px] font-bold text-white shadow-brand">
                        3
                    </span>
                </button>

                {/* Theme toggle */}
                <button
                    type="button"
                    onClick={toggleTheme}
                    data-testid="topbar-theme-toggle"
                    className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-border text-foreground hover:bg-muted transition-colors"
                    aria-label="Tema değiştir"
                >
                    {theme === "dark" ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
                </button>

                {/* User menu */}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            type="button"
                            data-testid="topbar-user-menu"
                            className="flex items-center gap-2 rounded-xl border border-border bg-card px-1.5 py-1 hover:bg-muted transition-colors"
                        >
                            <Avatar className="h-8 w-8">
                                <AvatarImage src={user?.avatar_url || undefined} alt={user?.full_name} />
                                <AvatarFallback className="bg-[hsl(var(--brand))] text-white text-[11px] font-bold">
                                    {initials(user?.full_name)}
                                </AvatarFallback>
                            </Avatar>
                            <div className="hidden md:block pr-1 text-left">
                                <div className="text-[12.5px] font-semibold leading-tight">{user?.full_name || "Kullanıcı"}</div>
                                <div className="text-[10.5px] text-muted-foreground leading-tight">{user?.title || "—"}</div>
                            </div>
                            <ChevronDown className="hidden md:block h-3.5 w-3.5 text-muted-foreground" />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-64">
                        <DropdownMenuLabel className="font-normal">
                            <div className="space-y-0.5">
                                <p className="text-sm font-semibold">{user?.full_name}</p>
                                <p className="text-xs text-muted-foreground">{user?.email}</p>
                            </div>
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                            <UserCircle className="mr-2 h-4 w-4" />
                            <span>{tr.common.profile}</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                            <SettingsIcon className="mr-2 h-4 w-4" />
                            <span>{tr.common.settings}</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                            <HelpCircle className="mr-2 h-4 w-4" />
                            <span>Yardım Merkezi</span>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            onClick={handleLogout}
                            data-testid="menu-logout"
                            className="text-[hsl(var(--destructive))] focus:text-[hsl(var(--destructive))]"
                        >
                            <LogOut className="mr-2 h-4 w-4" />
                            <span>{tr.common.signOut}</span>
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </header>
    );
}
